import debounce from 'lodash.debounce';
import { ChangeEvent, useEffect, useRef, useState } from 'react';
import { useSnapshot } from 'valtio';

import Input from '@cartwhl/ui/atoms/Input';
import Loading from '@cartwhl/ui/atoms/Loading';
import Text from '@cartwhl/ui/base/Text';
import BorderedCheckmark from '@cartwhl/ui/icons/BorderedCheckmark';
import Button from '@cartwhl/ui/input/Button';

import { authStore, noticesStore, userStore } from 'stores/global';

import UserDetails from './UserDetails';

export default function AccountDetails() {
  const { loading: userLoading, userInfo } = useSnapshot(userStore.state);

  if (userLoading) {
    return (
      <div className="overview max-w-(--breakpoint-md) mx-auto">
        <div className="animate-pulse">
          <div className="h-24 w-24 rounded-full bg-gray-200" />
          <div className="mt-4 h-6 w-48 bg-gray-200" />
          <div className="mt-2 h-4 w-32 bg-gray-200" />
        </div>
        <div className="mt-8">
          <div className="h-6 w-32 bg-gray-200" />
          <div className="mt-4 rounded-lg border p-6">
            <div className="h-8 w-full bg-gray-200" />
            <div className="mt-4 h-8 w-full bg-gray-200" />
          </div>
        </div>
        <div className="mt-8">
          <div className="h-6 w-32 bg-gray-200" />
          <div className="mt-4 rounded-lg border p-6">
            <div className="h-8 w-full bg-gray-200" />
            <div className="mt-4 h-8 w-full bg-gray-200" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="overview max-w-(--breakpoint-md) mx-auto">
      <div className="flex flex-row flex-nowrap">
        <UserDetails userInfo={userInfo} />
      </div>
      <div className="mt-8">
        <Text as="h3" variant="studio/body/medium/sm" className="mb-4">
          Details
        </Text>
        <div className="border-gray-30 dark:border-gray-60 rounded-lg border p-6">
          <div className="flex w-full flex-row flex-nowrap gap-4">
            {/* TODO: Add loading animation */}
            <InlineInput
              className="grow"
              label="First name"
              name="first-name"
              defaultValue={userInfo.firstName}
              onChange={async (e) => {
                await userStore.update({ firstName: e.target.value });
                return true;
              }}
            />
            <InlineInput
              className="grow"
              label="Last name"
              name="last-name"
              defaultValue={userInfo.lastName}
              onChange={async (e) => {
                await userStore.update({ lastName: e.target.value });
                return true;
              }}
            />
          </div>
          <div className="mt-6 max-w-[50%]">
            <InlineInput
              label="Display name"
              name="display-name"
              defaultValue={userInfo.preferredName}
              onChange={async (e) => {
                await userStore.update({ preferredName: e.target.value });
                return true;
              }}
            />
          </div>
        </div>
      </div>
      <div className="mt-8">
        <Text as="h3" variant="studio/body/medium/sm" className="mb-4">
          Account security
        </Text>
        <div className="border-gray-30 dark:border-gray-60 rounded-lg border p-6">
          <div className="border-gray-30 dark:border-gray-60 flex w-full flex-row flex-nowrap items-end gap-4 border-b pb-8">
            <div>
              <Text
                as="label"
                variant="studio/body/medium/sm"
                className=" flex flex-col gap-2.5 "
              >
                Email
              </Text>
              <Text
                id="user-email"
                as="p"
                variant="studio/body/medium/md"
                className=" py-3 "
              >
                {userInfo.email}
              </Text>
            </div>
            {userInfo.emailVerified && (
              <Text
                className="grow py-3 opacity-50 dark:opacity-70"
                variant="web/body/md"
              >
                <BorderedCheckmark className="-mt-0.5 mr-1 inline h-4 w-4" />
                Verified
              </Text>
            )}
            {!userInfo.emailVerified && (
              <Button
                className="ml-auto"
                variant="secondary"
                onClick={() => {
                  authStore.verifyEmail(userInfo.email).then((success) => {
                    if (success) {
                      noticesStore.info(
                        'Check your email for a login request and verification link!',
                      );
                    }
                  });
                }}
              >
                Send verification email
              </Button>
            )}
          </div>
          <div className="flex w-full flex-row flex-nowrap items-end gap-4 pt-8">
            {/* <Input
              label="Password"
              name="password"
              defaultValue={'•••••••••••'}
              disabled
            /> */}
            <div>
              <Text
                as="label"
                variant="web/body/medium/md"
                className=" flex flex-col gap-2.5 "
              >
                Password
              </Text>
              <Text
                id="user-email"
                as="p"
                variant="web/body/md"
                className=" py-3 "
              >
                •••••••••••
              </Text>
            </div>
            <Button
              className="ml-auto"
              onClick={() => authStore.requestPasswordReset(userInfo.email)}
            >
              Reset password
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

function InlineInput({
  className,
  label,
  name,
  defaultValue,
  onChange,
  errorMessage = 'Unable to update',
  timeout = 2000,
}: {
  className?: string;
  label: string;
  name: string;
  defaultValue: string;
  onChange: (e: ChangeEvent<HTMLInputElement>) => Promise<boolean>;
  errorMessage?: string;
  timeout?: number;
}) {
  const domElement = useRef<null | HTMLDivElement>(null);
  const [isLoading, setIsLoading] = useState(false);
  const changed = debounce((e: ChangeEvent<HTMLInputElement>) => {
    setIsLoading(true);
    onChange(e).then((success) => {
      setIsLoading(false);
      if (!success) {
        const input = domElement.current?.querySelector('input');
        if (input) {
          input.setCustomValidity(errorMessage);
        }
      }
    });
  }, timeout);

  return (
    <div
      ref={domElement}
      className={`relative ${typeof className === 'string' ? className : ''}`}
    >
      <Input
        label={label}
        name={name}
        disabled={isLoading}
        defaultValue={defaultValue}
        onChange={changed}
      />
      {isLoading && (
        <Loading className="pointer-events-none absolute bottom-2.5 right-2" />
      )}
    </div>
  );
}
